import {useTaggingLoad} from "@/hooks/common/useTagging";
import {getCookie, getQueryParams} from "@COMPONENTS/common/commonUtil";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import {goodsTpLabel} from "@COMPONENTS/common/goodsUtil";
import Breadcrumb from "@COMPONENTS/layout/breadcrumbs/desktop";
import Footer from "@COMPONENTS/layout/footer/Desktop";
import Header from "@COMPONENTS/layout/header/Desktop";
import TaggingGTM from "@COMPONENTS/product/pages/detailPackage/common/GTMTags";
import {PageProps} from "@COMPONENTS/product/pages/detailPackage/common/types";
import DetailContents from "@COMPONENTS/product/pages/detailPackage/desktop/DetailContents";
import DetailHeader from "@COMPONENTS/product/pages/detailPackage/desktop/DetailHeader";
import DetailPromotion from "@COMPONENTS/product/pages/detailPackage/desktop/DetailPromotion";
import DetailRecommend from "@COMPONENTS/product/pages/detailPackage/desktop/DetailRecommend";
import DetailSchedule from "@COMPONENTS/product/pages/detailPackage/desktop/DetailSchedule";
import DetailSNB from "@COMPONENTS/product/pages/detailPackage/desktop/DetailSnb";
import {TDetailPackage} from "@COMPONENTS/product/types";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {Container, DescriptionContent, DescriptionWrap} from "@FRAMEWORK/ui/desktop/layout";
import getSEO from "@FRAMEWORK/utils/seo";
import useConfigureAxios from "@FRAMEWORK/utils/useConfigureAxios";
import useCustomerMe from "@QUERIES/account/useCustomerMe";
import {EventDetail, eventDetailSSR} from "@QUERIES/prdt/event/useEventDetail";
import {GoodsDetail, goodsDetailSSR} from "@QUERIES/prdt/goods/useGoodsDetail";
import {GoodsImage, goodsImageSSR} from "@QUERIES/prdt/goods/useGoodsImage";
import goodsLowestPriceEventSSR from "@QUERIES/prdt/goods/useGoodsLowestPriceEvent";
import {goodsRecentEventSSR} from "@QUERIES/prdt/goods/useGoodsRecentEvent";
import {GoodsVideo, goodsVideoSSR} from "@QUERIES/prdt/goods/useGoodsVideo";
import {useQueryClient} from "@tanstack/react-query";
import dayjs from "dayjs";
import {GetServerSidePropsContext} from "next";
import {useEffect} from "react";
import styled from "styled-components";

interface SSRProps {
	eventDetail: EventDetail;
	goodsDetail: GoodsDetail;
	goodsImage: GoodsImage[];
	goodsVideo: GoodsVideo[];
}

const Wrap = styled.div`
	@media print {
		display: none;

		#printPopup {
			display: block;
		}
	}
`;

/* 최근 본 상품 저장 **/
const daykor = ["일", "월", "화", "수", "목", "금", "토"];

const Page = (props: TDetailPackage) => {
	/** 예외처리 */
	useEffect(() => {
		if (props.message) {
			alert(props.message);
			location.href = `${publicRuntimeConfig.ENV_PORTAL_URL}`;
		}
	}, []);
	if (props.message) return <></>;

	/** 정상 */
	const {query, eventDetail, goodsImage} = props;

	/** 로그인 정보 */
	const signin = useCustomerMe();
	const webCustNo = signin.data?.body?.webCustNo || "";

	const queryClient = useQueryClient();

	/** 최근 본 상품 SET */
	const setRecently = async (eventDetail: EventDetail, goodsImage: GoodsImage[], webCustId = "") => {
		const userId = getCookie("userId");
		if (!userId) return;

		const {evCd, evStartDt, outDeprtTm, evArriveDt, inArrvTm, trCompanySnm, trCompanyNm, trTp} = eventDetail;
		const {pathname} = location;
		const queries = getQueryParams();
		const search = `?menu=${queries.menu}&dspSid=${queries.dspSid}&evCd=${evCd}`;
		const detailedUrl = `${publicRuntimeConfig.ENV_PRODUCT_URL}${pathname}${search}`;
		const imgUrl = goodsImage[0]?.imageThum3 || "";
		const goodsId = eventDetail.evCd ?? "";
		const goodsName = eventDetail.evNm ?? "";
		const type = eventDetail.goodsTp === "D" ? "DMT" : "PKG";
		const viewDate = dayjs().format("YYYY.MM.DD HH:mm:ss");
		const category = goodsTpLabel(eventDetail.goodsTp).join(";");
		const soldoutYn = "";

		const trafficCode = {A: trCompanySnm, T: "TRAIN", S: "SHIP", B: "BUS"}[trTp] || "ETC";
		const dayOfWeekStart = daykor[dayjs(evStartDt).day()];
		const dayOfWeekArrive = daykor[dayjs(evArriveDt).day()];
		const outDeprtTime = outDeprtTm ? `${outDeprtTm.slice(0, 2)}:${outDeprtTm.slice(2)}` : "";
		const inArrvTime = inArrvTm ? `${inArrvTm.slice(0, 2)}:${inArrvTm.slice(2)}` : "";
		const information =
			`${dayjs(evStartDt).format("YYYY.MM.DD")};(${dayOfWeekStart});${outDeprtTime};` +
			`${dayjs(evArriveDt).format("YYYY.MM.DD")};(${dayOfWeekArrive});${inArrvTime};` +
			`${trafficCode};${trTp ? trCompanyNm : "교통불포함"}`;

		try {
			const result = await useConfigureAxios({
				url: `${publicRuntimeConfig.ENV_ESSENTIAL_API_BASEPATH}/api/user/recentlyViewed`,
				method: "POST",
				data: {
					imgUrl,
					goodsId,
					goodsName,
					information,
					type,
					viewDate,
					userId,
					detailedUrl,
					webCustId,
					category,
					soldoutYn,
				},
			});

			if (result.data.code === "0000") queryClient.refetchQueries({queryKey: ["useGetRecently"], type: "active"});
		} catch (e) {
			console.log(e);
		}
	};

	useEffect(() => {
		if (!signin.isFetching) {
			setRecently(eventDetail, goodsImage, webCustNo);
		}
	}, [signin]);

	// tagging
	useTaggingLoad("pkg_view_dtl", eventDetail);

	return (
		<>
			<Wrap>
				<Header />
				<Container>
					<Breadcrumb />
					{/* header */}
					<DetailHeader {...props} />
					<DescriptionWrap>
						{/* snb */}
						<DetailSNB eventDetail={eventDetail} />
						<DescriptionContent>
							{/* 여행 주요일정, 상품가, 사용 가능 쿠폰 */}
							<DetailContents {...props} />
							{/* 상세 스케줄 */}
							<DetailSchedule {...query} />
							{/* 관련 기획전 */}
							<DetailPromotion {...query} />
						</DescriptionContent>
					</DescriptionWrap>
					{/* 추천상품 */}
					<DetailRecommend {...query} />
				</Container>
				<Footer />
			</Wrap>
			<ErrorBoundary>
				<TaggingGTM {...props} />
			</ErrorBoundary>
		</>
	);
};

const getServerSidePropsSSR = async (evCd: string): Promise<SSRProps> => {
	const goodsCd = evCd.substring(0, 7);

	const eventDetailPromise = await eventDetailSSR(evCd);
	const eventDetail = eventDetailPromise.data.body || null;

	const goodsDetailPromise = await goodsDetailSSR(goodsCd);
	const goodsDetail = goodsDetailPromise.data.body || null;

	const goodsImagePromise = await goodsImageSSR(goodsCd);
	const goodsImage = goodsImagePromise.data.body || [];

	const goodsVideoPromise = await goodsVideoSSR(goodsCd);
	const goodsVideo = goodsVideoPromise.data.body || [];

	return {
		goodsImage,
		goodsVideo,
		eventDetail,
		goodsDetail,
	};
};

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
	// SEO
	let seo = {};
	let data: any = {};
	const message = "판매 종료된 상품입니다. 메인 페이지로 이동합니다.";

	try {
		const {query} = ctx;

		const dspSid = (query?.dspSid as string) ?? "";
		const pageQuery: PageProps["query"] = {...(query as PageProps["query"])};
		if (!pageQuery.evCd && !pageQuery?.goodsCd) {
			return {
				props: {
					message,
				},
			};
		}
		// goodsCd 가 있는 경우
		if (!pageQuery?.evCd) {
			if (pageQuery.goodsMonth) {
				const lowestPriceEventRes = await goodsLowestPriceEventSSR(pageQuery.goodsCd, pageQuery.goodsMonth);
				pageQuery.evCd = lowestPriceEventRes.data.body.evCd;
			} else {
				const availEventRes = await goodsRecentEventSSR(pageQuery.goodsCd, "amt", pageQuery.startDate);
				pageQuery.evCd = availEventRes.data.body.evCd;
			}

			data = await getServerSidePropsSSR(pageQuery.evCd);
			seo = await getSEO(dspSid, "goodsCd", pageQuery.goodsCd);
			if (!data.eventDetail || !data.goodsDetail) {
				return {
					props: {
						message,
					},
				};
			}
			// evCd가 있는 경우우
		} else {
			data = await getServerSidePropsSSR(pageQuery.evCd);
			seo = await getSEO(dspSid, "evCd", pageQuery.evCd);
			if (!data.eventDetail || !data.goodsDetail) {
				return {
					props: {
						message,
						...seo,
					},
				};
			}
		}
		return {
			props: {
				query: pageQuery,
				...seo,
				...data,
			},
		};
	} catch (error) {
		return {
			props: {
				message,
			},
		};
	}
}

export default Page;
